import { Action, createActionGroup, createFeature, createFeatureSelector, createReducer, emptyProps, on } from '@ngrx/store'
import { PublicEventData } from 'globals/store/public.model';
import { ShareableItemType, ShareableState, ShareableItem, ShareableInfoKeyMap, PublishInfo, ShareOSSInfo, SessionItemInfo, StandItemInfo, PublicEditEntityAssetsInfo } from 'globals/store/share.data';
import { starLog } from '../helpers/helpers';
import { jsonCopyObject, unknownDateToDate } from 'globals/helpers/helpers';
import { createHomePageData } from 'globals/store/publish.data';
import { eventsActions } from './events.era';
import { userActions } from './user.era';

function initialStateCreator():ShareableState{
  return { // put in alpha order
    asset:{},
    attendee: {},
    attendeeBrief: {},
    attendeeView: {},
    cues: {},
    eAsset: {},
    entityAsset: {},
    eRequest: {},
    exhibitor: {},
    exhibitorBrief: {},
    logistics: {},
    notes: {},
    oss:{},
    performer:{},
    profile:{},
    publish:{},
    published_data: {},
    quote: {},
    resource: {},
    resourceBrief: {},
    sponsor: {},
    stagingRequest: {},
    synopsis: {},
  }
}
export const shareActions = createActionGroup({
  source: 'Share',
  events: {
    'Load or update shareables': (shareables: ShareableItem[]) => ({ shareables }),
    'Load shareable oss': (id:string, item:ShareOSSInfo) => ({ id, item }),
    'Load shareable publish': (code:string, item:PublishInfo) => ({ code, item }),
    'Add published data': (publishCode:string, payload: PublicEventData) => ({ publishCode, payload }),
    'Remove shareable': (key:string, shareableType: ShareableItemType) => ({ key, shareableType }),
    'Event list unload': emptyProps
  }
})

const loadShareables = (state: ShareableState, shareables: ShareableItem[]): ShareableState => {

  state = jsonCopyObject(state) // a shallow copy is not sufficient to detect changes, and would rather do this than each inner object destructuring
  shareables = jsonCopyObject(shareables) // also copy shareables to avoid modifying the original objects that are in the store, which could cause weird bugs where the store is modified without dispatching an action. This way if we modify anything we know it's only modifying the copy and not the original object in the store. This is especially important for things like publish info which can have a lot of nested properties that could be modified in various places.
  const usedStateKeys: Set<string> = new Set(shareables.map(s => s.type)) 
  usedStateKeys.forEach(key => state[key] = { ...state[key] }) // needs to be done before so old state isn't modified directly
  shareables.forEach(shareableItem => {
    let key: string
    if (shareableItem.info) {
      key = shareableItem.info[ShareableInfoKeyMap[shareableItem.type]]
    } else {
      key = shareableItem.code
    }
    if (!key) {
      console.log("UPDATE THE SHAREABLE INFO KEY MAP (SHAREABLE NOT LOADED!)", { shareableItem, ShareableInfoKeyMap, infoKey: ShareableInfoKeyMap[shareableItem.type] })
      return
    }
    
    switch (shareableItem.type) {
      case 'publish':
        const publishInfo = jsonCopyObject(shareableItem.info)
        publishInfo.eventId = shareableItem.eventId
        publishInfo.accountId = shareableItem.accountId
        publishInfo.publishCode = key
        if (!publishInfo.homePageData) publishInfo.homePageData = createHomePageData()
        const { publishedAt } = publishInfo
        if (!shareableItem.accountId) {
          starLog(`Missing accountId for PublishInfo shareable [${shareableItem.code}]`) // <== the handling of account id and shareables is quite messy, so checking if there is an id here.
          console.log("Shareable: ", shareableItem)
        }
        if (publishedAt) publishInfo.publishedAt = unknownDateToDate(publishedAt)
        state.publish[key] = publishInfo
        break;
      case 'oss':
        state.oss[key] = shareableItem.info
        break;
      case 'profile': // performer public edit profile
        shareableItem.info['code'] = shareableItem.code
        state.profile[key] = shareableItem.info
        break;
      case 'sponsor': // sponsor public edit profile
        shareableItem.info['code'] = shareableItem.code
        state.sponsor[key] = shareableItem.info
        break
      case 'exhibitor': // exhibitor public edit profile
        shareableItem.info['code'] = shareableItem.code
        state.exhibitor[key] = shareableItem.info
        break
      case 'attendee':
        shareableItem.info['code'] = shareableItem.code
        state.attendee[key] = shareableItem.info
        break
      case 'performer': // performer brief
        shareableItem.info['code'] = shareableItem.code
        state.performer[key] = shareableItem.info
        break;
      case 'resource':
        shareableItem.info['code'] = shareableItem.code
        state.resource[key] = shareableItem.info
        break
      case 'attendeeBrief': {
        const info = shareableItem.info
        state.attendeeBrief[info.attendeeId] = info
        break;
      }
      case 'attendeesView': {
        const info = shareableItem.info
        state.attendeeView[info.eventId] = info
        break;
      }
      case 'exhibitorBrief': {
        const info = shareableItem.info
        state.exhibitorBrief[info.exhibitorId] = info
        break;
      }
      case 'resourceBrief': {
        const info = shareableItem.info
        state.resourceBrief[info.resourceId] = info
        break;
      }
      case 'asset': // performer session asset
        state.asset[key] = shareableItem.info
        break;
      case 'synopsis':
        state.synopsis[key] = shareableItem.info
        break;
      case 'stagingRequest': // performer staging request
        state.stagingRequest[key] = shareableItem.info
        break
      case 'eAsset': // exhibitor stand asset
        state.eAsset[key] = shareableItem.info
        break;
      case 'entityAsset':
        state.entityAsset[key] = shareableItem.info
        state.entityAsset[key].editableAssetIds = state.entityAsset[key].editableAssetIds || []
        break
      case 'eRequest':  // exhibitor stand request
        state.eRequest[key] = shareableItem.info
        break
      case 'notes':
        state.notes[key] = shareableItem.info
        break;
      case 'cues':
        state.cues[key] = shareableItem.info
        break;         
      case 'quote':
        state.quote[key] = shareableItem.info
        break
      case 'published_data':
        console.log(shareableItem.type + " not implemented yet")
        break
      case 'events':
        console.log(shareableItem.type + " not implemented yet")
        break
      case 'logistics':
        state.logistics[shareableItem.code] = shareableItem
        break
      default:
        console.log("MISSING TYPE!", { shareableItem })
        break
    }
  })
  return state
}
export const shareReducer = createReducer(
  initialStateCreator(),
  on(shareActions.loadOrUpdateShareables, (state, { shareables }) => loadShareables(state, shareables)),
  on(shareActions.loadShareableOss, (state, { id, item }) => ({ ...state, oss:{...state.oss, [id]:item}})),
  on(shareActions.loadShareablePublish, (state, { code, item }) => ({ ...state, publish:{...state.publish, [code]:item }})),
  on(shareActions.addPublishedData, (state, { publishCode, payload }) => {
    const published_data = state.published_data ? {...state.published_data} : {}
    if (payload) published_data[publishCode] = payload
    else delete published_data[publishCode]
    return {...state, published_data }
  }),
  on(shareActions.removeShareable, (state, { key, shareableType }) => {
    const typeObject = {...state[shareableType]}
    delete typeObject[key]
    return {
      ...state,
      [shareableType]: typeObject
    }
  }),
  on(shareActions.eventListUnload, (state) => initialStateCreator()),
  on(eventsActions.unloadEvent, (state) => initialStateCreator()),
  on(userActions.logout, (state) => initialStateCreator()),
)

export const shareFeature = createFeature({
  name: 'shareables',
  reducer: shareReducer,
});

export const shareStateSelector = createFeatureSelector<ShareableState>(shareFeature.name)
